import React, { useState, useEffect } from "react";
import { Card, Table, Button, Modal, Select, InputNumber, message, Alert } from "antd";
import axiosInstance from "../../../axiosInstance";
import { useParams, useNavigate } from "react-router-dom";

const ProductBatchMovement = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [product, setProduct] = useState(null);
    const [batches, setBatches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [modalVisible, setModalVisible] = useState(false);
    const [fromBatch, setFromBatch] = useState(null);
    const [toBatch, setToBatch] = useState(null);
    const [quantity, setQuantity] = useState("");

    // ==========================
    // Load product + batches
    // ==========================
    useEffect(() => {
        loadPage();
    }, []);

    const loadPage = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get(`/products/${id}/move-quantity-data`);
            setProduct(res.data.product);
            setBatches(res.data.batches);
            console.log(res.data);
        } catch (err) {
            if (err.response?.status === 404) {
                setError("Product not found in the selected shop. Please check if you have the correct shop selected.");
                message.error("Product not found in the selected shop");
            } else if (err.response?.data?.error) {
                setError(err.response.data.error);
                message.error(err.response.data.error);
            } else {
                setError("Failed to load product data. Please try again.");
                message.error("Failed to load product data");
            }
        } finally {
            setLoading(false);
        }
    };

    // ==========================
    // Open modal for a batch
    // ==========================
    const openModal = (batchId) => {
        setFromBatch(batchId);
        setToBatch(null);
        setQuantity("");
        setModalVisible(true);
    };

    // ==========================
    // Submit movement
    // ==========================
    const submitMovement = async () => {
        if (!quantity) return message.error("Enter quantity");

        try {
            await axiosInstance.post("/products/move-quantity", {
                product_id: product.id,
                from_batch_id: fromBatch,
                to_batch_id: toBatch,
                quantity: quantity,
                shopId: product.shop_id
            });

            message.success("Quantity moved successfully!");

            setModalVisible(false);
            loadPage(); // reload updated stock

        } catch (err) {
            message.error(err.response?.data?.error || "Movement failed.");
        }
    };

    // ==========================
    // Table structure
    // ==========================
    const columns = [
        {
            title: "Batch",
            dataIndex: "label",
        },
        {
            title: "Quantity",
            dataIndex: "quantity",
        },
        {
            title: "Expiry",
            dataIndex: "expire_date",
            render: v => v || "--"
        },
        {
            title: "Buying Price",
            dataIndex: "buying_price",
            render: (value, row) => {
                const price = value || product?.buying_price;
                return price ? `${price}` : "--";
            }
        },
        {
            title: "Selling Price",
            dataIndex: "selling_price",
            render: (value, row) => {
                const price = value || product?.selling_price;
                return price ? `${price}` : "--";
            }
        },
        {
            title: "Action",
            render: (_, row) => (
                <Button type="primary" onClick={() => openModal(row.id)}>
                    Move Quantity
                </Button>
            )
        }
    ];

    const rows = [
        {
            key: "default",
            id: null,
            quantity: product?.quantity,
            buying_price: product?.buying_price,
            selling_price: product?.selling_price,
            label: "Default Stock"
        },
        ...batches.map(b => ({
            key: b.id,
            id: b.id,
            quantity: b.quantity,
            expire_date: b.expire_date,
            buying_price: b.buying_price,
            selling_price: b.selling_price,
            label: `Batch ${b.id}`
        }))
    ];

    // Show error state if product not found
    if (error) {
        return (
            <Card>
                <Alert
                    message="Product Not Available"
                    description={error}
                    type="error"
                    showIcon
                    action={
                        <Button type="primary" onClick={() => navigate('/products')}>
                            Back to Products
                        </Button>
                    }
                />
            </Card>
        );
    }

    return (
        <Card loading={loading} title={`Move Quantity — ${product?.name || 'Loading...'}`}>
            <Table columns={columns} dataSource={rows} pagination={false} />

            <Modal
                title="Move Quantity"
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                onOk={submitMovement}
            >
                <p><strong>From:</strong> {fromBatch ? `Batch ${fromBatch}` : "Default Stock"}</p>

                <p className="mt-2 mb-1">Move To:</p>
                <Select
                    style={{ width: "100%" }}
                    placeholder="Select destination"
                    value={toBatch}
                    onChange={(v) => setToBatch(v)}
                >
                    <Select.Option value={null}>Default Stock</Select.Option>
                    {batches.map(b => (
                        <Select.Option key={b.id} value={b.id}>
                            Batch {b.id} — Qty {b.quantity}
                        </Select.Option>
                    ))}
                </Select>

                <p className="mt-3 mb-1">Quantity to Move:</p>
                <InputNumber
                    style={{ width: "100%" }}
                    min={1}
                    value={quantity}
                    onChange={setQuantity}
                />
            </Modal>
        </Card>
    );
};

export default ProductBatchMovement;